import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/system';
import { useTheme } from 'styled-components';
import { formatDecimal } from '@app/common';
import { Tooltip } from '../../../../components/app/tooltip/tooltip';
import { CompoundInterestSummaryHero } from './hero/compound-interest-summary-hero';
import { CompoundInterestSummaryFooter } from './footer/compound-interest-summary-footer';
import {
    CompoundInterestSummary as SummaryType,
    CompoundInterestParams,
} from '../../../../utils/compound-interest/types';

interface CompoundInterestSummaryProps {
    summary: SummaryType;
    params: CompoundInterestParams;
}

export const CompoundInterestSummary: React.FC<CompoundInterestSummaryProps> = ({ summary, params }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery('(max-width: 599px)');
    const { currency } = params;
    const isProfitPositive = summary.profitAfterTax > 0;
    const isRoiPositive = summary.roi > 0;
    const taxAmountPrefix = summary.taxAmount > 0 ? '-' : '';
    const amountFractionDigits = isMobile ? 0 : 2;
    const formatCurrencyAmount = (amount: number) => `${formatDecimal(amount, amountFractionDigits)} ${currency}`;

    return (
        <div className="flex flex-col">
            <section className="flex flex-col gap-0">
                <CompoundInterestSummaryHero
                    finalValueLabel={t('pages.compoundInterest.summary.finalValue')}
                    finalValue={formatCurrencyAmount(summary.finalValue)}
                    totalInvestedLabel={t('pages.compoundInterest.summary.totalInvested')}
                    totalInvested={formatCurrencyAmount(summary.totalInvested)}
                    profitAfterTaxLabel={t('pages.compoundInterest.summary.profitAfterTax')}
                    profitAfterTax={`${isProfitPositive ? '+' : ''}${formatCurrencyAmount(summary.profitAfterTax)}`}
                />
                <CompoundInterestSummaryFooter
                    roiLabel={t('pages.compoundInterest.summary.roi')}
                    roiValue={`${isRoiPositive ? '+' : ''}${formatDecimal(summary.roi, 1)}%`}
                    roiValueStyle={{
                        color: isRoiPositive ? theme.palette.color.positive.main : theme.palette.common.text,
                    }}
                    taxAmountLabel={
                        params.taxRate === 0 ? (
                            <Tooltip
                                icon
                                content={t('pages.compoundInterest.calculator.zeroTaxRateTooltip')}
                                position="top"
                            >
                                {t('pages.compoundInterest.summary.taxAmount')}
                            </Tooltip>
                        ) : (
                            t('pages.compoundInterest.summary.taxAmount')
                        )
                    }
                    taxAmountValue={`${taxAmountPrefix}${formatCurrencyAmount(summary.taxAmount)}`}
                    realValueLabel={
                        <Tooltip icon content={t('pages.compoundInterest.summary.realValueTooltip')} position="top">
                            {t('pages.compoundInterest.summary.realValue')}
                        </Tooltip>
                    }
                    realValue={formatCurrencyAmount(summary.realValue)}
                />
            </section>
        </div>
    );
};
